import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as AuthActions from '../../store/actions/auth';

const getHeaderValue = (headers, name) => {
  if (!headers) return null;

  const loweredName = name.toLowerCase();

  if (typeof headers.get === 'function') {
    return headers.get(name);
  }

  if (Array.isArray(headers)) {
    const matchedHeader = headers.find(([headerName]) => {
      return headerName && headerName.toLowerCase() === loweredName;
    });
    return matchedHeader ? matchedHeader[1] : null;
  }

  if (typeof headers === 'object') {
    const matchedKey = Object.keys(headers).find((headerName) => {
      return headerName.toLowerCase() === loweredName;
    });
    return matchedKey ? headers[matchedKey] : null;
  }

  return null;
};

const requestHasAuthorization = (input, init) => {
  const initHeader = getHeaderValue(init && init.headers, 'Authorization');
  if (initHeader) return true;

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return !!getHeaderValue(input.headers, 'Authorization');
  }

  return false;
};

const requestUsesCurrentSessionToken = (input, init) => {
  const authorization = getHeaderValue(init && init.headers, 'Authorization')
    || (typeof Request !== 'undefined' && input instanceof Request
      ? getHeaderValue(input.headers, 'Authorization')
      : null);
  const activeToken = sessionStorage.getItem('token');

  return Boolean(authorization && activeToken && authorization === `Token ${activeToken}`);
};

export const clinicianHasAccess = ({ role, user }) => {
  const roleType = role && role.role_type ? String(role.role_type).toLowerCase() : null;

  if (roleType === 'patient') return false;
  if (roleType) return true;
  if (!user || user.has_profile === true) return false;

  return Boolean(
    user.is_staff === true ||
    user.admin === true ||
    user.care_provider ||
    user.care_provider_unit
  );
};

const useClinicianAuthGuard = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const token = useSelector((state) => state.auth.token);
  const role = useSelector((state) => state.auth.role);
  const user = useSelector((state) => state.auth.user);
  const logoutStartedRef = useRef(false);

  const hasAccess = useMemo(() => {
    return clinicianHasAccess({ role, user });
  }, [role, user]);

  const isAuthorizedClinician = Boolean(isLoggedIn && token && hasAccess);

  const logoutAndRedirect = useCallback(async () => {
    if (logoutStartedRef.current) return;
    logoutStartedRef.current = true;

    try {
      await dispatch(AuthActions.logout());
    } catch (error) {
      dispatch({ type: AuthActions.LOGOUT });
    }

    window.location.replace('/');
  }, [dispatch]);

  useEffect(() => {
    if (isAuthorizedClinician) {
      logoutStartedRef.current = false;
      return;
    }

    void logoutAndRedirect();
  }, [isAuthorizedClinician, logoutAndRedirect]);

  useEffect(() => {
    if (!isAuthorizedClinician || typeof window.fetch !== 'function') {
      return undefined;
    }

    const originalFetch = window.fetch.bind(window);
    const wrappedFetch = async (input, init) => {
      const response = await originalFetch(input, init);

      if (requestHasAuthorization(input, init) && response.status === 401 && requestUsesCurrentSessionToken(input, init)) {
        void logoutAndRedirect();
      }

      return response;
    };

    window.fetch = wrappedFetch;

    return () => {
      if (window.fetch === wrappedFetch) {
        window.fetch = originalFetch;
      }
    };
  }, [isAuthorizedClinician, logoutAndRedirect]);

  return {
    isAuthorizedClinician,
    isLoggedIn,
    token,
  };
};

export default useClinicianAuthGuard;